import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Navbar from "../components/Navbar";
import SeatPlanImageModal from "../components/SeatPlanImageModal";
import QRCode from "react-qr-code";
import { jsPDF } from "jspdf";
import QRCodeLib from "qrcode";
import {
  buildPickerSeatId,
  getVenueSeatConfig,
  resolveEventSectionLayout,
} from "../lib/eventSeatLayout";
import { resolveCheckoutSeatArea } from "../lib/seatSectionDisplay";
import { getPaymentCreateCheckoutUrl } from "../lib/paymentApiUrl";
import { getComputedEventStatus, isPublicTicketEvent } from "../lib/eventPublicStatus";
import { isTierSoldOut } from "../lib/eventAvailability";
import { columnHeadersForPage, resolveSeatNumber } from "../lib/venueLayout";
import { isVenuePolicyEmpty, resolveEventPolicyHtml } from "../lib/venuePolicyHtml";
import {
  availableSeatFillStyle,
  DEFAULT_SECTION_COLOR,
  getSnapshotSectionColor,
  normalizeHex,
  resolveSectionColor,
} from "../lib/sectionColor";
import { fetchVenueSectionColors } from "../lib/venuesApi";
import {
  MAX_TICKETS_PER_ACCOUNT_PER_EVENT,
  PURCHASE_LIMIT_MESSAGE,
  fetchAccountPurchaseQuota,
} from "../lib/ticketPurchaseLimit";
import {
  CHECKOUT_HOLD_MINUTES,
  SEAT_CLAIM_AUTH_MESSAGE,
  SEAT_CLAIM_TAKEN_MESSAGE,
  SEAT_HOLD_EXPIRED_MESSAGE,
  buildTakenSeatsByTier,
  buildTakenSeatsFromFallback,
  claimSeatHold,
  extendSeatHolds,
  pruneSelectedSeatsAgainstTaken,
  releaseMyEventSeatHolds,
  releaseSeatHold,
  seatClaimKey,
  seatsPayloadFromSelectedByTier,
} from "../lib/seatClaim";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { formatDisplayAmount } from "../lib/money";

const pendingSeatHoldReleaseTimers = new Map();

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [eventNotFound, setEventNotFound] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState("");
  const [retryingFetch, setRetryingFetch] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [, setQuantities] = useState({});
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [quantityError, setQuantityError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [showSeatMapModal, setShowSeatMapModal] = useState(false);
  const [seatModalTierId, setSeatModalTierId] = useState(null);
  const [selectedSeatsByTier, setSelectedSeatsByTier] = useState({});
  const [takenSeatsByTier, setTakenSeatsByTier] = useState({});
  const [claimingSeatKeys, setClaimingSeatKeys] = useState(() => new Set());
  const [seatSelectionError, setSeatSelectionError] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const isPayingRef = useRef(false);
  const seatModalBaselineRef = useRef({});
  const selectedSeatsByTierRef = useRef({});
  const claimingSeatKeysRef = useRef(new Set());
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [createdTickets] = useState([]);
  const [activeReceiptIndex, setActiveReceiptIndex] = useState(0);
  const [venueSectionColors, setVenueSectionColors] = useState({});
  const [accountTicketRemaining, setAccountTicketRemaining] = useState(
    MAX_TICKETS_PER_ACCOUNT_PER_EVENT
  );

  const selectionStorageKey = `echo-jham-ticket-selection:${id}`;

  const normalizeSeatLabel = (value) => String(value || "").trim();

  const currentSeatModalTier = tiers.find((tier) => tier.id === seatModalTierId) || null;

  const currentVenueConfig = getVenueSeatConfig(event?.venue);
  const resolvedSeatLayout = resolveEventSectionLayout({
    event,
    tier: currentSeatModalTier,
  });
  const currentSectionLayout = resolvedSeatLayout.currentSectionLayout;
  const useCanonicalLabels = resolvedSeatLayout.useCanonicalLabels;

  const currentPage = currentSectionLayout?.pages ? currentSectionLayout.pages[currentPageIndex] : null;
  const currentSectionColor = resolveSectionColor({
    tierColor: currentSeatModalTier?.color,
    snapshotColor: currentSectionLayout?.color || currentPage?.color,
    venueColor:
      venueSectionColors[currentSeatModalTier?.section] ||
      venueSectionColors[currentSectionLayout?.label],
  });
  const availableSeatStyle = availableSeatFillStyle(currentSectionColor);

  const parsedSeatSnapshot =
    typeof event?.seat_layout_snapshot === "string"
      ? (() => {
          try {
            return JSON.parse(event.seat_layout_snapshot);
          } catch {
            return null;
          }
        })()
      : event?.seat_layout_snapshot || null;

  const getTierSectionColor = (tier) =>
    resolveSectionColor({
      tierColor: tier?.color,
      snapshotColor: getSnapshotSectionColor(parsedSeatSnapshot, tier?.section || tier?.name),
      venueColor: venueSectionColors[tier?.section] || venueSectionColors[tier?.name],
    });

  const seatRows = currentPage?.rows ?? currentSectionLayout?.rows ?? currentVenueConfig.rows;
  const seatCols = currentPage
    ? columnHeadersForPage(currentPage)
    : currentSectionLayout?.rowSeatCounts
    ? Array.from(
        {
          length: Math.max(0, ...Object.values(currentSectionLayout.rowSeatCounts)),
        },
        (_, index) => index + 1
      )
    : currentVenueConfig.cols;
  const rowSeatCounts = currentPage ? null : currentSectionLayout?.rowSeatCounts || null;
  const seatLayout = currentPage?.layout ?? null;

  // Fixed seat sizing keeps seats readable while horizontal scroll prevents layout compression.
  const responsiveSeatSize = 40;
  const responsiveSeatWidth = `${responsiveSeatSize}px`;
  const seatGapClass = "gap-2";
  const seatTextClass = "text-[8px] md:text-[9px] leading-none";
  const seatLabelClass = "text-xs md:text-sm";
  const seatAxisLabelWidth = "40px";
  const selectedSeatCount = currentSeatModalTier ? getSelectedSeats(currentSeatModalTier.id).length : 0;
  const selectedSeatPrice = Number(currentSeatModalTier?.price || 0);
  const seatSubtotal = selectedSeatPrice * selectedSeatCount;
  const seatServiceFee = 0;
  const seatGrandTotal = seatSubtotal + seatServiceFee;

  const getTierRemainingStock = useCallback((tier) => {
    const quantityAvailable = Number(tier.quantity_available || 0);
    const quantitySold = Number(tier.quantity_sold || 0);

    if (quantityAvailable <= 0) {
      return null;
    }

    return Math.max(quantityAvailable - quantitySold, 0);
  }, []);

  const exampleTakenSeats = new Set(["A3", "A4", "B7", "D2", "E9", "F5"]);

  function getSelectedSeats(tierId) {
    return selectedSeatsByTier[tierId] || [];
  }

  const getTakenSeats = (tierId) => takenSeatsByTier[tierId] || [];

  const markSeatClaiming = (key, claiming) => {
    if (claiming) claimingSeatKeysRef.current.add(key);
    else claimingSeatKeysRef.current.delete(key);
    setClaimingSeatKeys(new Set(claimingSeatKeysRef.current));
  };

  const openSeatModal = async (tier) => {
    if (!isPublicTicketEvent(event) || isTierSoldOut(tier)) return;

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user?.id) {
      navigate("/login", { state: { from: `/event/${id}` } });
      return;
    }

    setSelectedTierId(tier.id);
    setSeatModalTierId(tier.id);
    setSeatSelectionError("");
    setCurrentPageIndex(0);
    seatModalBaselineRef.current = {
      ...seatModalBaselineRef.current,
      [tier.id]: [...(selectedSeatsByTierRef.current[tier.id] || [])],
    };
    setShowSeatModal(true);
  };

  const toggleSeatSelection = async (tierId, seatId, seatArea = "") => {
    const claimKey = seatClaimKey(tierId, seatId);
    if (claimingSeatKeysRef.current.has(claimKey)) return;

    const current = selectedSeatsByTierRef.current[tierId] || [];
    const isSelected = current.some((seat) => seat.id === seatId);

    if (isSelected) {
      markSeatClaiming(claimKey, true);
      try {
        await releaseSeatHold(supabase, {
          eventId: id,
          ticketTierId: tierId,
          seatLabel: seatId,
        });
        setSelectedSeatsByTier((prev) => {
          const nextSeats = (prev[tierId] || []).filter((seat) => seat.id !== seatId);
          if (nextSeats.length === 0) {
            const { [tierId]: _, ...rest } = prev;
            return rest;
          }
          return { ...prev, [tierId]: nextSeats };
        });
        setSeatSelectionError("");
      } finally {
        markSeatClaiming(claimKey, false);
      }
      return;
    }

    const otherCount = Object.entries(selectedSeatsByTierRef.current).reduce((sum, [entryId, seats]) => {
      if (entryId === tierId) return sum;
      return sum + (seats?.length || 0);
    }, 0);
    if (otherCount + current.length + 1 > accountTicketRemaining) {
      setSeatSelectionError(PURCHASE_LIMIT_MESSAGE);
      return;
    }

    markSeatClaiming(claimKey, true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) {
        setSeatSelectionError(SEAT_CLAIM_AUTH_MESSAGE);
        return;
      }

      const claim = await claimSeatHold(supabase, {
        eventId: id,
        ticketTierId: tierId,
        seatLabel: seatId,
        seatArea,
      });

      if (!claim.ok) {
        if (claim.reason === "auth_required") {
          setSeatSelectionError(SEAT_CLAIM_AUTH_MESSAGE);
        } else {
          setSeatSelectionError(SEAT_CLAIM_TAKEN_MESSAGE);
          setTakenSeatsByTier((prev) => {
            const existing = new Set(prev[tierId] || []);
            existing.add(normalizeSeatLabel(seatId));
            return { ...prev, [tierId]: Array.from(existing) };
          });
        }
        return;
      }

      setSeatSelectionError("");
      setSelectedSeatsByTier((prev) => {
        const existing = prev[tierId] || [];
        if (existing.some((seat) => seat.id === seatId)) return prev;
        return {
          ...prev,
          [tierId]: [
            ...existing,
            { id: seatId, label: seatId, seatArea: String(seatArea || "").trim() },
          ],
        };
      });
    } finally {
      markSeatClaiming(claimKey, false);
    }
  };

  const confirmSeatSelection = () => {
    if (!currentSeatModalTier) {
      return;
    }

    const selectedSeats = getSelectedSeats(currentSeatModalTier.id);
    if (!selectedSeats.length) {
      setSeatSelectionError("Please select at least one seat.");
      return;
    }

    setQuantities((prev) => ({
      ...prev,
      [currentSeatModalTier.id]: selectedSeats.length,
    }));

    seatModalBaselineRef.current = {
      ...seatModalBaselineRef.current,
      [currentSeatModalTier.id]: [...selectedSeats],
    };
    setSelectedTierId(currentSeatModalTier.id);
    setSeatSelectionError("");
    setShowSeatModal(false);
  };

  const closeSeatSelectionPage = () => {
    const tierId = seatModalTierId;
    const current = tierId ? selectedSeatsByTierRef.current[tierId] || [] : [];

    if (tierId) {
      seatModalBaselineRef.current = {
        ...seatModalBaselineRef.current,
        [tierId]: [...current],
      };

      setQuantities((prev) => {
        if (!current.length) {
          const { [tierId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [tierId]: current.length };
      });
    }

    setSeatSelectionError("");
    setShowSeatModal(false);
  };

  const cancelSeatSelectionPage = async () => {
    const tierId = seatModalTierId;
    const current = tierId ? selectedSeatsByTierRef.current[tierId] || [] : [];

    if (tierId && current.length) {
      await Promise.all(
        current.map((seat) =>
          releaseSeatHold(supabase, {
            eventId: id,
            ticketTierId: tierId,
            seatLabel: seat.id,
          })
        )
      );

      setSelectedSeatsByTier((prev) => {
        const { [tierId]: _, ...rest } = prev;
        return rest;
      });
      setQuantities((prev) => {
        const { [tierId]: _, ...rest } = prev;
        return rest;
      });
      seatModalBaselineRef.current = {
        ...seatModalBaselineRef.current,
        [tierId]: [],
      };
    }

    setSeatSelectionError("");
    setShowSeatModal(false);
  };

  const getSeatStatus = (seatId) => {
    if (!useCanonicalLabels && exampleTakenSeats.has(seatId)) {
      return "taken";
    }

    const currentTierId = currentSeatModalTier?.id;
    if (currentTierId && claimingSeatKeys.has(seatClaimKey(currentTierId, seatId))) {
      return "claiming";
    }

    if (currentTierId && getTakenSeats(currentTierId).includes(seatId)) {
      return "taken";
    }

    const selectedSeats = currentSeatModalTier ? getSelectedSeats(currentSeatModalTier.id) : [];
    return selectedSeats.some((seat) => seat.id === seatId) ? "selected" : "available";
  };
  const restoreSelection = useCallback((availableTiers) => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const raw = window.localStorage.getItem(selectionStorageKey);
      if (!raw) {
        return;
      }

      const saved = JSON.parse(raw);
      const savedTierId = saved?.selectedTierId;
      const savedQuantity = Number(saved?.quantity);

      if (!savedTierId || !Number.isInteger(savedQuantity) || savedQuantity <= 0) {
        return;
      }

      const savedTier = availableTiers.find((tier) => tier.id === savedTierId);
      const quantityAvailable = Number(savedTier?.quantity_available || 0);
      const quantitySold = Number(savedTier?.quantity_sold || 0);
      if (!savedTier || (quantityAvailable > 0 && quantitySold >= quantityAvailable)) {
        return;
      }

      const maxAllowed = getTierRemainingStock(savedTier);
      const nextQuantity = maxAllowed == null ? savedQuantity : Math.min(savedQuantity, maxAllowed);

      setSelectedTierId(savedTierId);
      setQuantities(
        availableTiers.reduce((accumulator, tier) => {
          accumulator[tier.id] = tier.id === savedTierId ? nextQuantity : 0;
          return accumulator;
        }, {})
      );

      if (nextQuantity !== savedQuantity) {
        setQuantityError(`Only ${nextQuantity} ticket${nextQuantity === 1 ? "" : "s"} available for this type.`);
      }
    } catch (error) {
      console.warn("Unable to restore ticket quantity selection:", error);
    }
  }, [selectionStorageKey, getTierRemainingStock]);

  const refreshTakenSeats = useCallback(async (tierIds = null) => {
    const effectiveTierIds =
      Array.isArray(tierIds) && tierIds.length > 0
        ? tierIds
        : (tiers || []).map((tier) => tier.id).filter(Boolean);

    if (!id || effectiveTierIds.length === 0) {
      return {};
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();
    const viewerUserId = user?.id || null;

    const { data: takenSeatRows, error: takenSeatError } = await supabase.rpc(
      "get_taken_seats_for_event",
      { p_event_id: id }
    );

    let nextTaken;
    if (takenSeatError) {
      console.error("Failed to fetch taken seats via RPC, falling back:", takenSeatError);
      const [soldTicketsResult, seatHoldsResult] = await Promise.all([
        supabase
          .from("tickets")
          .select("ticket_tier_id, seat_label")
          .in("ticket_tier_id", effectiveTierIds)
          .not("seat_label", "is", null)
          .neq("seat_label", ""),
        supabase
          .from("seat_holds")
          .select("ticket_tier_id, seat_label, hold_status, expires_at, reserved_by")
          .eq("event_id", id),
      ]);

      nextTaken = buildTakenSeatsFromFallback({
        tickets: soldTicketsResult.data || [],
        holds: seatHoldsResult.data || [],
        viewerUserId,
      });
    } else {
      nextTaken = buildTakenSeatsByTier(takenSeatRows || [], { viewerUserId });
    }

    setTakenSeatsByTier(nextTaken);

    setSelectedSeatsByTier((prev) => {
      const { next, changed } = pruneSelectedSeatsAgainstTaken(prev, nextTaken);
      return changed ? next : prev;
    });

    return nextTaken;
  }, [id, tiers]);

  const fetchEvent = useCallback(async ({ showRetryState = false } = {}) => {
    if (showRetryState) {
      setRetryingFetch(true);
    }

    const { data: eventData, error: eventError } = await supabase
      .from("events")
      .select("*")
      .eq("id", id)
      .neq("status", "archived")
      .maybeSingle();

    if (eventError) {
      console.error("Failed to fetch event details:", eventError);
      setServerError(true);
      setServerErrorMessage("Unable to load event details right now.");
      setEvent(null);
      setEventNotFound(false);
      setTiers([]);
      setQuantities({});
      setSelectedTierId(null);
      setQuantityError("");
      setVenueSectionColors({});

      if (showRetryState) {
        setRetryingFetch(false);
      }
      return;
    }

    if (!eventData) {
      setServerError(false);
      setServerErrorMessage("");
      setEvent(null);
      setEventNotFound(true);
      setTiers([]);
      setQuantities({});
      setSelectedTierId(null);
      setQuantityError("");

      if (showRetryState) {
        setRetryingFetch(false);
      }
      return;
    }

    const computedStatus = getComputedEventStatus(eventData);
    if (computedStatus === "draft" || computedStatus === "archived") {
      setServerError(false);
      setServerErrorMessage("");
      setEvent(null);
      setEventNotFound(true);
      setTiers([]);
      setQuantities({});
      setSelectedTierId(null);
      setQuantityError("");

      if (showRetryState) {
        setRetryingFetch(false);
      }
      return;
    }

    const { data: tiersData, error: tiersError } = await supabase
      .from("ticket_tiers")
      .select("*")
      .eq("event_id", id);

    if (tiersError) {
      console.error("Failed to fetch ticket tiers:", tiersError);
      setServerError(true);
      setServerErrorMessage("Unable to load ticket information. Please retry.");
      setEvent(null);
      setEventNotFound(false);
      setTiers([]);
      setQuantities({});
      setSelectedTierId(null);
      setQuantityError("");

      if (showRetryState) {
        setRetryingFetch(false);
      }
      return;
    }

    setServerError(false);
    setServerErrorMessage("");
    setEventNotFound(false);
    setEvent(eventData);

    if (eventData.venue_id) {
      const venueSections = await fetchVenueSectionColors(supabase, [eventData.venue_id]);
      const nextColors = {};
      venueSections.forEach((section) => {
        if (section.parent_section_id) return;
        const hex = normalizeHex(section.color);
        if (hex) nextColors[section.name] = hex;
      });
      setVenueSectionColors(nextColors);
    } else {
      setVenueSectionColors({});
    }

    const takenSeatsByTierNext = {};
    const tierIds = (tiersData || []).map((tier) => tier.id).filter(Boolean);
    const soldCountByTier = {};

    if (tierIds.length > 0) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const viewerUserId = user?.id || null;

      const { data: takenSeatRows, error: takenSeatError } = await supabase.rpc(
        "get_taken_seats_for_event",
        { p_event_id: id }
      );

      if (takenSeatError) {
        console.error("Failed to fetch taken seats via RPC, falling back:", takenSeatError);
        const [soldTicketsResult, seatHoldsResult] = await Promise.all([
          supabase
            .from("tickets")
            .select("ticket_tier_id, seat_label")
            .in("ticket_tier_id", tierIds)
            .not("seat_label", "is", null)
            .neq("seat_label", ""),
          supabase
            .from("seat_holds")
            .select("ticket_tier_id, seat_label, hold_status, expires_at, reserved_by")
            .eq("event_id", id),
        ]);

        Object.assign(
          takenSeatsByTierNext,
          buildTakenSeatsFromFallback({
            tickets: soldTicketsResult.data || [],
            holds: seatHoldsResult.data || [],
            viewerUserId,
          })
        );
      } else {
        Object.assign(
          takenSeatsByTierNext,
          buildTakenSeatsByTier(takenSeatRows || [], { viewerUserId })
        );
      }

      (tiersData || []).forEach((tier) => {
        soldCountByTier[tier.id] = Number(tier.quantity_sold || 0);
      });
    }

    const normalizedTiers = (tiersData || []).map((tier) => ({
      ...tier,
      quantity_sold: Math.max(
        Number(tier.quantity_sold || 0),
        Number(soldCountByTier[tier.id] || 0)
      ),
    }));

    setTiers(normalizedTiers);
    setTakenSeatsByTier(takenSeatsByTierNext);

    const initial = {};
    normalizedTiers.forEach((t) => (initial[t.id] = 0));
    setQuantities(initial);
    setSelectedTierId(null);
    setQuantityError("");
    restoreSelection(normalizedTiers);

    if (showRetryState) {
      setRetryingFetch(false);
    }
  }, [id, restoreSelection]);

  useEffect(() => {
    fetchEvent();
  }, [id, fetchEvent]);

  useEffect(() => {
    selectedSeatsByTierRef.current = selectedSeatsByTier;
  }, [selectedSeatsByTier]);

  useEffect(() => {
    isPayingRef.current = isPaying;
  }, [isPaying]);

  useEffect(() => {
    if (!id) return undefined;

    const channel = supabase
      .channel(`event-seat-lock-${id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tickets" },
        () => {
          refreshTakenSeats();
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "seat_holds", filter: `event_id=eq.${id}` },
        () => {
          refreshTakenSeats();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [id, refreshTakenSeats]);

  useEffect(() => {
    if (!showSeatModal || !id) return undefined;

    const pollId = window.setInterval(() => {
      refreshTakenSeats();
    }, 18000);

    return () => {
      window.clearInterval(pollId);
    };
  }, [showSeatModal, id, refreshTakenSeats]);

  useEffect(() => {
    if (!id) return undefined;

    const pending = pendingSeatHoldReleaseTimers.get(id);
    if (pending) {
      window.clearTimeout(pending);
      pendingSeatHoldReleaseTimers.delete(id);
    }

    return () => {
      if (isPayingRef.current) return;
      const timer = window.setTimeout(() => {
        pendingSeatHoldReleaseTimers.delete(id);
        releaseMyEventSeatHolds(supabase, id).catch((error) => {
          console.warn("Unable to release seat holds on leave:", error);
        });
      }, 400);
      pendingSeatHoldReleaseTimers.set(id, timer);
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const loadPurchaseQuota = async () => {
      if (!id || !event) {
        setAccountTicketRemaining(MAX_TICKETS_PER_ACCOUNT_PER_EVENT);
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user?.id) {
        if (!cancelled) setAccountTicketRemaining(MAX_TICKETS_PER_ACCOUNT_PER_EVENT);
        return;
      }

      try {
        const quota = await fetchAccountPurchaseQuota(supabase, {
          userId: user.id,
          eventId: id,
        });
        if (!cancelled) {
          setAccountTicketRemaining(quota.remaining);
        }
      } catch (error) {
        console.warn("Unable to load ticket purchase quota:", error);
        if (!cancelled) {
          setAccountTicketRemaining(MAX_TICKETS_PER_ACCOUNT_PER_EVENT);
        }
      }
    };

    loadPurchaseQuota();

    return () => {
      cancelled = true;
    };
  }, [id, event]);

  useEffect(() => {
    setSelectedSeatsByTier((prev) => {
      let remaining = accountTicketRemaining;
      const next = {};
      let trimmed = false;

      Object.entries(prev).forEach(([tierId, seats]) => {
        if (!Array.isArray(seats) || seats.length === 0) return;
        if (remaining <= 0) {
          trimmed = true;
          return;
        }
        const kept = seats.slice(0, remaining);
        if (kept.length !== seats.length) trimmed = true;
        remaining -= kept.length;
        if (kept.length > 0) next[tierId] = kept;
      });

      return trimmed ? next : prev;
    });
  }, [accountTicketRemaining]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleOnline = () => {
      if (serverError || !event) {
        fetchEvent({ showRetryState: true });
        return;
      }

      restoreSelection(tiers);
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [event, serverError, tiers, fetchEvent, restoreSelection]);

  useEffect(() => {
    if (!eventNotFound) return;

    const redirectTimer = setTimeout(() => {
      navigate("/", {
        replace: true,
        state: {
          homeNotice: "Event no longer available",
        },
      });
    }, 1800);

    return () => clearTimeout(redirectTimer);
  }, [eventNotFound, navigate]);

  const selectTier = (tier) => {
    if (isTierSoldOut(tier)) return;
    openSeatModal(tier);
  };

  const selectedTickets = tiers.filter(
    (t) => (selectedSeatsByTier[t.id]?.length || 0) > 0
  );

  const selectedTicketGroups = selectedTickets.map((tier) => {
    const seats = selectedSeatsByTier[tier.id] || [];
    return {
      tier,
      seats,
      seatCount: seats.length,
      subtotal: Number(tier.price || 0) * seats.length,
    };
  });

  const fallbackPageLabelForTier = (tier) => {
    const layout = resolveEventSectionLayout({ event, tier }).currentSectionLayout;
    const pages = layout?.pages || [];
    if (pages.length === 1) return String(pages[0]?.label || "").trim();
    if (tier?.id === currentSeatModalTier?.id) return String(currentPage?.label || "").trim();
    return "";
  };

  const totalSeatCount = selectedTicketGroups.reduce((sum, group) => sum + group.seatCount, 0);

  const total = selectedTickets.reduce(
    (sum, t) => sum + t.price * (selectedSeatsByTier[t.id]?.length || 0),
    0
  );

  const activeTicket = createdTickets[activeReceiptIndex] || null;
  const hasMultipleReceipts = createdTickets.length > 1;

  const buildReceiptPdf = async (ticket, ticketIndex, totalTickets, existingDoc = null) => {
    const doc = existingDoc || new jsPDF({ unit: "pt", format: "a4" });

    if (existingDoc) {
      doc.addPage();
    }

    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(220, 0, 0);
    doc.rect(0, 0, pageWidth, 48, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("TICKET DETAILS", 24, 30);

    doc.setFillColor(245, 245, 245);
    doc.roundedRect(36, 72, pageWidth - 72, 640, 8, 8, "F");

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Ticket ${ticketIndex + 1} of ${totalTickets}`, pageWidth / 2, 64, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("ECHO JHAM", pageWidth / 2, 102, { align: "center" });

    doc.setFontSize(14);
    doc.text((event.title || "EVENT TITLE").toUpperCase(), 60, 130);

    doc.setDrawColor(210, 210, 210);
    doc.line(60, 138, pageWidth - 60, 138);

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "normal");
    doc.text("DATE", 60, 160);
    doc.text("TICKET TYPE", pageWidth - 180, 160);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(`${event.date || ""} | ${event.time || ""}`.trim(), 60, 176);
    doc.text(ticket.tierName || "GENERAL ADMISSION", pageWidth - 180, 176);

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "normal");
    doc.text("VENUE", 60, 198);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text((event.venue || "").toUpperCase(), 60, 214);

    if (ticket.tierSection) {
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.setFont("helvetica", "normal");
      doc.text("SECTION", 60, 236);

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text(ticket.tierSection.toUpperCase(), 60, 250);
    }

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "normal");
    doc.text("ROW - SEAT", pageWidth - 180, 236);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(ticket.seatLabel || ticket.seat_label || "-", pageWidth - 180, 250);

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "normal");
    doc.text("ATTENDEE", 60, 266);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(ticket.attendeeName || "Guest", 60, 262);

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "normal");
    doc.text("ORDER NO.", 60, 292);
    doc.text("PRICE", pageWidth - 180, 292);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(String(ticket.id), 60, 308);
    doc.text(`PHP ${Number(ticket.tierPrice || 0).toLocaleString()}`, pageWidth - 180, 308);

    doc.setDrawColor(190, 190, 190);
    doc.setLineDashPattern([2, 2], 0);
    doc.line(60, 334, pageWidth - 60, 334);
    doc.setLineDashPattern([], 0);

    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "normal");
    doc.text("TICKET ID", pageWidth / 2, 354, { align: "center" });

    const qrDataUrl = await QRCodeLib.toDataURL(ticket.qr_code, {
      margin: 1,
      width: 220,
    });
    doc.addImage(qrDataUrl, "PNG", (pageWidth - 120) / 2, 366, 120, 120);

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text(ticket.manualTicketId, pageWidth / 2, 504, { align: "center" });

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont("helvetica", "normal");
    doc.text(`Backup QR: ${ticket.qr_code}`, pageWidth / 2, 520, { align: "center" });

    return doc;
  };

  const handleDownloadSinglePdf = async () => {
    if (!activeTicket || isDownloadingPdf) return;

    try {
      setIsDownloadingPdf(true);
      const doc = await buildReceiptPdf(activeTicket, activeReceiptIndex, createdTickets.length);
      doc.save(`ticket-${activeTicket.manualTicketId}.pdf`);
    } catch (error) {
      console.error(error);
      alert("Failed to generate PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleDownloadAllPdf = async () => {
    if (!createdTickets.length || isDownloadingPdf) return;

    try {
      setIsDownloadingPdf(true);
      let doc = null;

      for (let i = 0; i < createdTickets.length; i++) {
        const ticket = createdTickets[i];
        doc = await buildReceiptPdf(ticket, i, createdTickets.length, doc);
      }

      if (!doc) {
        throw new Error("No tickets available for export");
      }

      doc.save(`tickets-${event.title || "echo-jham"}.pdf`);
    } catch (error) {
      console.error(error);
      alert("Failed to generate PDF download");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handlePayMongoCheckout = async () => {
    if (isPaying) return;
    if (!isPublicTicketEvent(event)) {
      alert("This event is no longer on sale.");
      return;
    }

    if (selectedTickets.length === 0 || totalSeatCount <= 0) {
      alert("Select at least 1 ticket");
      return;
    }

    if (totalSeatCount > accountTicketRemaining) {
      alert(PURCHASE_LIMIT_MESSAGE);
      return;
    }

    try {
      setIsPaying(true);

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser?.id) {
        throw new Error("You must be logged in to start payment.");
      }

      const selectedTicketSummary = selectedTicketGroups.map((group) => ({
        ticket_tier_id: group.tier.id,
        quantity: group.seatCount,
        seat_labels: group.seats.map((seat) => normalizeSeatLabel(seat.label)).filter(Boolean),
        seat_areas: group.seats.map((seat) =>
          resolveCheckoutSeatArea({
            section: group.tier.section || group.tier.name,
            seatArea: seat.seatArea,
            seatLabel: seat.label,
            fallbackPageLabel: fallbackPageLabelForTier(group.tier),
          })
        ),
      }));

      const holdSeats = seatsPayloadFromSelectedByTier(selectedSeatsByTier);
      if (holdSeats.length > 0) {
        const extended = await extendSeatHolds(supabase, {
          eventId: id,
          seats: holdSeats,
          minutes: CHECKOUT_HOLD_MINUTES,
        });
        if (!extended.ok) {
          throw new Error(SEAT_HOLD_EXPIRED_MESSAGE);
        }
      }

      let paymentOrderId;
      if (authUser?.id) {
        const { data: paymentOrder, error: paymentOrderError } = await supabase
          .from("payment_orders")
          .insert({
            user_id: authUser.id,
            event_id: id,
            amount: Number(total),
            currency: "PHP",
            selected_tickets: selectedTicketSummary,
          })
          .select("id")
          .single();

        if (paymentOrderError || !paymentOrder?.id) {
          throw new Error(paymentOrderError?.message || "Unable to create pending payment.");
        }

        paymentOrderId = paymentOrder.id;
      }

      const paymentApiUrl = getPaymentCreateCheckoutUrl();
      const response = await fetch(paymentApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: total,
          description: `${event.title} ticket purchase`,
          payment_order_id: paymentOrderId,
        }),
      });
      const result = await response.json();

      if (!response.ok || !result.checkout_url) {
        throw new Error(result.error || "Unable to start payment.");
      }

      window.location.assign(result.checkout_url);
    } catch (error) {
      console.error("Unable to start PayMongo checkout:", error);
      alert(error.message || "Unable to start payment. Please try again.");
      setIsPaying(false);
    }
  };


  if (eventNotFound) {
    return (
      <div className="min-h-screen text-white bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-white/5 border border-white/10 rounded-3xl p-8">
          <p className="text-sm uppercase tracking-widest text-gray-200">Event no longer available</p>
          <p className="text-xs text-gray-500 mt-3">Redirecting to homepage...</p>
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 bg-brand-maroon hover:bg-red-700 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (serverError) {
    return (
      <div className="min-h-screen text-white bg-[#0a0a0a] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center bg-white/5 border border-white/10 rounded-3xl p-8">
          <p className="text-sm uppercase tracking-widest text-gray-200">{serverErrorMessage || "Something went wrong."}</p>
          <button
            type="button"
            onClick={() => fetchEvent({ showRetryState: true })}
            disabled={retryingFetch}
            className="mt-6 bg-brand-maroon hover:bg-red-700 disabled:opacity-60 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest"
          >
            {retryingFetch ? "Retrying..." : "Retry"}
          </button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen text-white bg-[#0a0a0a]">
        <Navbar />
        <section className="relative py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="h-64 rounded-2xl bg-white/5 animate-pulse" />
          </div>
        </section>
      </div>
    );
  }

  const ticketsOnSale = isPublicTicketEvent(event);
  const policyHtml = resolveEventPolicyHtml(event);

  if (showSeatModal && currentSeatModalTier && ticketsOnSale) {
    return (
      <div className="min-h-screen text-white bg-[#0a0a0a]">
        <Navbar />

        <section className="bg-gradient-to-b from-[#0f0f0f] via-[#151515] to-[#1b1b1b] py-6 md:py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">Seat Selection</p>
                <h1 className="text-2xl md:text-3xl font-black uppercase leading-tight">{event.title}</h1>
                <p className="text-sm text-white/70 mt-1">
                  {currentSeatModalTier.name}
                  {currentSeatModalTier.section ? ` - ${currentSeatModalTier.section}` : ""}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowSeatMapModal(true)}
                  className="px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-xs md:text-sm font-bold uppercase tracking-[0.12em]"
                >
                  View Seat Plan
                </button>
                <button
                  type="button"
                  onClick={closeSeatSelectionPage}
                  className="px-4 py-2.5 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 text-xs md:text-sm font-bold uppercase tracking-[0.12em]"
                >
                  Back To Event
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#121212] p-3 md:p-4">
              <div className="flex flex-wrap gap-2">
                {tiers.map((tier) => {
                  const isCurrentTier = tier.id === currentSeatModalTier.id;
                  const soldOut = isTierSoldOut(tier);

                  return (
                    <button
                      key={tier.id}
                      type="button"
                      disabled={soldOut}
                      onClick={() => {
                        seatModalBaselineRef.current = {
                          ...seatModalBaselineRef.current,
                          [tier.id]: [...(selectedSeatsByTierRef.current[tier.id] || [])],
                        };
                        setSeatModalTierId(tier.id);
                        setSelectedTierId(tier.id);
                        setSeatSelectionError("");
                        setCurrentPageIndex(0);
                      }}
                      className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-bold uppercase tracking-[0.06em] transition ${
                        soldOut
                          ? "bg-gray-700/50 text-gray-500 cursor-not-allowed"
                          : isCurrentTier
                          ? "bg-red-600 text-white shadow-[0_0_18px_rgba(220,38,38,0.35)]"
                          : "bg-white/10 text-white/80 hover:bg-white/20"
                      }`}
                    >
                      {tier.section || tier.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-4 md:gap-6">
              <div className="rounded-3xl border border-white/10 bg-[#121212] p-4 md:p-6 space-y-4 md:space-y-5">
                <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm text-white/75">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-sm border inline-block"
                      style={availableSeatStyle}
                    />
                    {currentSeatModalTier?.section || currentSectionLayout?.label
                      ? `${currentSeatModalTier?.section || currentSectionLayout?.label} — available`
                      : "Available"}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-gray-600 border border-gray-500 inline-block" />
                    Taken
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-red-600 border border-red-300 inline-block" />
                    Selected
                  </div>
                </div>

                {currentSectionLayout?.pages && (
                  <div className="flex flex-wrap gap-2">
                    {currentSectionLayout.pages.map((page, index) => (
                      <button
                        key={page.id}
                        type="button"
                        onClick={() => setCurrentPageIndex(index)}
                        className={`px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-bold uppercase tracking-[0.08em] ${
                          currentPageIndex === index
                            ? "bg-red-600 text-white"
                            : "bg-white/10 text-white/70 hover:bg-white/20"
                        }`}
                      >
                        {page.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="rounded-2xl border border-white/10 bg-[#0b0b0b] px-4 py-3 text-center">
                  <p className="text-[10px] md:text-xs tracking-[0.35em] font-black text-white/80 uppercase">Stage</p>
                  <div className="h-1.5 md:h-2 w-full max-w-xl mx-auto mt-2 rounded-full bg-gradient-to-r from-red-700/40 via-red-500 to-red-700/40 shadow-[0_0_16px_rgba(239,68,68,0.55)]" />
                </div>

                <div className="rounded-3xl bg-[#101010] border border-white/10 p-3 md:p-4 overflow-x-auto overflow-y-hidden">
                  <div className="min-w-max w-max mx-auto space-y-2 md:space-y-2.5">
                    {seatLayout
                      ? seatRows.map((row, rowIndex) => (
                          <div
                            key={row}
                            className={`grid ${seatGapClass} items-center`}
                            style={{ gridTemplateColumns: `${seatAxisLabelWidth} repeat(${seatCols.length}, ${responsiveSeatWidth}) ${seatAxisLabelWidth}` }}
                          >
                            <div className={`text-center font-bold text-white/70 ${seatLabelClass}`}>{row}</div>
                            {seatLayout[rowIndex].map((isSeat, colIndex) => {
                              if (isSeat === 0) {
                                return <div key={`empty-${row}-${colIndex}`} />;
                              }

                              const seatNumber =
                                resolveSeatNumber(currentPage, rowIndex, colIndex) ?? colIndex + 1;
                              const seatLabel = `${row}${seatNumber}`;
                              const seatId = buildPickerSeatId({
                                useCanonicalLabels,
                                page: currentPage,
                                row,
                                column: seatNumber,
                              });
                              const status = getSeatStatus(seatId);
                              const isSelected = status === "selected";
                              const isTaken = status === "taken";
                              const isClaiming = status === "claiming";
                              const statusLabel = isTaken
                                ? "Taken"
                                : isClaiming
                                ? "Updating"
                                : isSelected
                                ? "Selected"
                                : "Available";

                              return (
                                <button
                                  key={seatId}
                                  type="button"
                                  disabled={isTaken || isClaiming}
                                  title={`${seatLabel} • ${statusLabel}`}
                                  aria-label={`Seat ${seatLabel}, ${statusLabel}`}
                                  style={{
                                    width: responsiveSeatWidth,
                                    height: responsiveSeatWidth,
                                    ...(isTaken || isSelected || isClaiming ? {} : availableSeatStyle),
                                  }}
                                  onClick={() => {
                                    if (isTaken || isClaiming) return;
                                    toggleSeatSelection(
                                      currentSeatModalTier.id,
                                      seatId,
                                      currentPage?.label || ""
                                    );
                                  }}
                                  className={`rounded-md border font-black ${seatTextClass} transform-gpu transition duration-150 hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ${
                                    isTaken
                                      ? "bg-gray-600 border-gray-500 text-white/80 cursor-not-allowed"
                                      : isClaiming
                                      ? "bg-gray-700 border-gray-500 text-white/70 cursor-wait opacity-80"
                                      : isSelected
                                      ? "bg-red-600 border-red-200 text-white shadow-[0_0_14px_rgba(239,68,68,0.6)]"
                                      : "text-white/90 hover:brightness-125"
                                  }`}
                                >
                                  {seatLabel}
                                </button>
                              );
                            })}
                            <div className={`text-center font-bold text-white/70 ${seatLabelClass}`}>{row}</div>
                          </div>
                        ))
                      : seatRows.map((row) => {
                          const count = rowSeatCounts?.[row] || seatCols.length;

                          return (
                            <div
                              key={row}
                              className={`grid ${seatGapClass} items-center`}
                              style={{ gridTemplateColumns: `${seatAxisLabelWidth} repeat(${seatCols.length}, ${responsiveSeatWidth}) ${seatAxisLabelWidth}` }}
                            >
                              <div className={`text-center font-bold text-white/70 ${seatLabelClass}`}>{row}</div>
                              {seatCols.map((col, index) => {
                                if (index >= count) {
                                  return <div key={`empty-${row}-${col}`} />;
                                }

                                const seatId = `${row}${col}`;
                                const status = getSeatStatus(seatId);
                                const isSelected = status === "selected";
                                const isTaken = status === "taken";
                                const isClaiming = status === "claiming";
                                const statusLabel = isTaken
                                  ? "Taken"
                                  : isClaiming
                                  ? "Updating"
                                  : isSelected
                                  ? "Selected"
                                  : "Available";

                                return (
                                  <button
                                    key={seatId}
                                    type="button"
                                    disabled={isTaken || isClaiming}
                                    title={`${seatId} • ${statusLabel}`}
                                    aria-label={`Seat ${seatId}, ${statusLabel}`}
                                    style={{
                                      width: responsiveSeatWidth,
                                      height: responsiveSeatWidth,
                                      ...(isTaken || isSelected || isClaiming ? {} : availableSeatStyle),
                                    }}
                                    onClick={() => {
                                      if (isTaken || isClaiming) return;
                                      toggleSeatSelection(
                                        currentSeatModalTier.id,
                                        seatId,
                                        currentPage?.label || ""
                                      );
                                    }}
                                    className={`rounded-md border font-black ${seatTextClass} transform-gpu transition duration-150 hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ${
                                      isTaken
                                        ? "bg-gray-600 border-gray-500 text-white/80 cursor-not-allowed"
                                        : isClaiming
                                        ? "bg-gray-700 border-gray-500 text-white/70 cursor-wait opacity-80"
                                        : isSelected
                                        ? "bg-red-600 border-red-200 text-white shadow-[0_0_14px_rgba(239,68,68,0.6)]"
                                        : "text-white/90 hover:brightness-125"
                                    }`}
                                  >
                                    {seatId}
                                  </button>
                                );
                              })}
                              <div className={`text-center font-bold text-white/70 ${seatLabelClass}`}>{row}</div>
                            </div>
                          );
                        })}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-[11px] md:text-xs text-white/55">
                </div>
              </div>

              <aside className="rounded-3xl border border-white/10 bg-[#121212] p-4 md:p-5 flex flex-col gap-4 h-fit xl:sticky xl:top-24">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-white/50">Your Selection</p>
                  <p className="text-3xl md:text-4xl font-black text-white mt-1">{selectedSeatCount}</p>
                  <p className="text-xs text-white/60 mt-1">seat{selectedSeatCount === 1 ? "" : "s"} selected</p>
                </div>

                <div className="rounded-2xl bg-[#0e0e0e] border border-white/10 p-3 max-h-52 overflow-y-auto no-scrollbar">
                  {selectedSeatCount > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {getSelectedSeats(currentSeatModalTier.id).map((seat) => (
                        <button
                          key={seat.id}
                          type="button"
                          onClick={() => toggleSeatSelection(currentSeatModalTier.id, seat.id)}
                          className="px-2.5 py-1.5 rounded-md bg-red-600/20 border border-red-500/50 text-red-100 text-xs font-bold hover:bg-red-600/35"
                          title={`Remove ${seat.label}`}
                        >
                          {seat.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-white/50">No seats selected yet.</p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between text-white/80">
                    <span>Ticket Type</span>
                    <span className="font-semibold text-white">{currentSeatModalTier.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-white/80">
                    <span>Seat Price</span>
                    <span className="font-semibold text-white">₱{formatDisplayAmount(selectedSeatPrice)}</span>
                  </div>
                  <div className="flex items-center justify-between text-white/80">
                    <span>Qty</span>
                    <span className="font-semibold text-white">{selectedSeatCount}</span>
                  </div>
                  <div className="flex items-center justify-between text-white/80">
                    <span>Subtotal</span>
                    <span className="font-semibold text-white">₱{formatDisplayAmount(seatSubtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-white/80">
                    <span>Service Fee</span>
                    <span className="font-semibold text-white">₱{formatDisplayAmount(seatServiceFee)}</span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                  <span className="text-sm text-white/70">Total</span>
                  <span className="text-2xl font-black text-white">₱{formatDisplayAmount(seatGrandTotal)}</span>
                </div>

                {seatSelectionError && <p className="text-xs text-red-400">{seatSelectionError}</p>}

                <button
                  type="button"
                  onClick={confirmSeatSelection}
                  disabled={selectedSeatCount === 0}
                  className={`w-full py-3 rounded-lg font-black uppercase tracking-[0.08em] transition ${
                    selectedSeatCount === 0
                      ? "bg-red-900/40 text-red-200/50 cursor-not-allowed"
                      : "bg-red-600 text-white hover:bg-red-700 shadow-[0_0_18px_rgba(220,38,38,0.45)]"
                  }`}
                >
                  Confirm Seats
                </button>

                <button
                  type="button"
                  onClick={cancelSeatSelectionPage}
                  className="w-full py-3 rounded-lg border border-white/15 text-sm font-bold uppercase hover:bg-white/5"
                >
                  Cancel
                </button>
              </aside>
            </div>
          </div>
        </section>

        <SeatPlanImageModal
          open={showSeatMapModal}
          onClose={() => setShowSeatMapModal(false)}
          title={event.title}
          imageUrl={event.seat_plan_image_url}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white bg-[#0a0a0a]">

      <Navbar />

      {/* HERO */}
      <section
        className="relative py-16"
        style={{
          backgroundImage: "url('/assets/home_featured_bg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/80" />

        <div className="relative max-w-6xl mx-auto px-6">
          <button
            type="button"
            onClick={() => navigate("/events")}
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white/80 hover:text-white mb-6"
          >
            <ArrowLeft size={16} />
            Events
          </button>

          <div className="flex flex-col md:flex-row items-center gap-12">
            <img
              src={event.image_url}
              alt={event.title}
              className="w-64 rounded-xl shadow-2xl"
            />

            <div>
              <p className="text-red-500 text-sm font-bold">
                {event.venue}
              </p>

              <h1 className="text-4xl md:text-5xl font-black uppercase mt-2">
                {event.title}
              </h1>

              <p className="text-gray-400 mt-2">
                {event.subtitle}
              </p>

              <div className="flex gap-3 mt-4">
                <span className="bg-red-600 px-3 py-1 text-xs font-bold rounded">
                  {event.date}
                </span>
                <span className="bg-red-600 px-3 py-1 text-xs font-bold rounded">
                  {event.time}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <section className="bg-gradient-to-b from-[#111] via-[#161616] to-[#1b1b1b] py-12">

        <div className="max-w-6xl mx-auto px-6">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* SEAT MAP */}
            <div>
              <h2 className="text-sm font-bold tracking-widest mb-4">
                SEAT MAP
              </h2>

              <div className="bg-white rounded-lg p-4">
                <img
                  src={event.seat_plan_image_url || "/assets/seatmap_sample.png"}
                  alt="Seat Map"
                  className="w-full"
                />
              </div>

              <button
                type="button"
                onClick={() => setShowSeatMapModal(true)}
                className="mt-4 bg-red-600 px-4 py-2 text-xs font-bold rounded hover:bg-red-700"
              >
                View Seat Map
              </button>
            </div>

            {/* TICKETS */}
            <div>
              <h2 className="text-sm font-bold tracking-widest mb-4">
                TICKET PRICES
              </h2>
              <p className="text-xs text-gray-400 mb-3">
                Max 4 tickets per account for this event. {accountTicketRemaining} remaining.
              </p>

              {!ticketsOnSale ? (
                <div className="rounded border border-white/10 bg-white/5 px-4 py-6 text-center">
                  <p className="text-sm font-bold uppercase tracking-widest text-white">This event has ended</p>
                  <p className="mt-2 text-xs text-gray-400">Tickets are no longer available for this show.</p>
                </div>
              ) : (
                <>
              <div className="grid grid-cols-3 text-xs text-gray-500 mb-2 px-2">
                <span>SECTION</span>
                <span className="text-center">PRICE</span>
                <span className="text-right">SEATS</span>
              </div>

              <div className="space-y-2">
                {tiers.map((tier) => {
                  const selectedSeats = selectedSeatsByTier[tier.id] || [];
                  const sectionColor = getTierSectionColor(tier) || DEFAULT_SECTION_COLOR;

                  return (
                    <div
                      key={tier.id}
                      className={`rounded border transition ${
                        selectedTierId === tier.id
                          ? "bg-white/10 border-white/20"
                          : "bg-white/5 border-transparent hover:border-white/10"
                      } ${isTierSoldOut(tier) ? "opacity-60" : ""}`}
                      style={{
                        borderLeftWidth: 6,
                        borderLeftColor: sectionColor,
                      }}
                    >
                      <div
                        onClick={() => selectTier(tier)}
                        className={`grid grid-cols-3 items-center px-4 py-3 cursor-pointer ${isTierSoldOut(tier) ? "cursor-not-allowed" : ""}`}
                      >
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{tier.section || tier.name}</p>
                          {tier.section && (
                            <p className="text-[11px] text-gray-400">{tier.name}</p>
                          )}
                        </div>

                        <span className="text-center text-gray-400">
                          ₱{formatDisplayAmount(tier.price)}
                        </span>

                        <div className="flex justify-end items-center gap-2">
                          {isTierSoldOut(tier) ? (
                            <span className="text-xs font-bold text-red-500">
                              Ticket type sold out
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSeatModal(tier);
                              }}
                              className="bg-red-600 px-3 py-1 text-xs font-bold uppercase rounded hover:bg-red-700"
                            >
                              Select Seats
                            </button>
                          )}
                        </div>
                      </div>

                      {selectedSeats.length > 0 && (
                        <div className="border-t border-white/10 px-4 py-3 text-xs text-gray-300">
                          <p className="font-semibold text-white text-[11px]">Selected seats</p>
                          <p>{selectedSeats.map((seat) => seat.label).join(", ")}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {quantityError && (
                <p className="mt-3 text-xs text-red-400">
                  {quantityError}
                </p>
              )}

              <button
                onClick={() => {
                  if (accountTicketRemaining <= 0) {
                    alert(PURCHASE_LIMIT_MESSAGE);
                    return;
                  }
                  if (selectedTickets.length === 0 || totalSeatCount <= 0) {
                    alert("Select one ticket type before purchase");
                    return;
                  }
                  if (totalSeatCount > accountTicketRemaining) {
                    alert(PURCHASE_LIMIT_MESSAGE);
                    return;
                  }
                  setShowModal(true);
                }}
                disabled={accountTicketRemaining <= 0}
                className="mt-6 w-full bg-red-600 py-3 rounded font-bold uppercase hover:bg-red-700 disabled:opacity-60 disabled:hover:bg-red-600"
              >
                Buy Ticket
              </button>
                </>
              )}
            </div>

          </div>

          {!isVenuePolicyEmpty(policyHtml) ? (
            <div className="mt-12 max-w-2xl min-w-0">
              <h2 className="text-sm font-bold tracking-widest mb-4 flex items-start gap-2">
                <ShieldCheck size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
                <span>VENUE POLICY &amp; GUIDELINES</span>
              </h2>

              <div
                className="text-gray-400 text-sm break-words [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-1 [&_strong]:font-bold [&_b]:font-bold [&_em]:italic [&_i]:italic [&_s]:line-through [&_strike]:line-through [&_del]:line-through"
                dangerouslySetInnerHTML={{ __html: policyHtml }}
              />
            </div>
          ) : null}

        </div>
      </section>

      {/* REVIEW MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">

          <div className="bg-[#2b2b2b] w-[90%] max-w-xl rounded-2xl p-6">

            <h2 className="text-center font-bold text-lg mb-6">
              REVIEW YOUR ORDER
            </h2>

            <div className="flex gap-4 mb-6">
              <img src={event.image_url} alt={event.title} className="w-24 h-32 rounded" />

              <div className="text-sm">
                <p><span className="text-gray-400">EVENT:</span> {event.title}</p>
                <p><span className="text-gray-400">DATE:</span> {event.date} {event.time}</p>
                <p><span className="text-gray-400">VENUE:</span> {event.venue}</p>
              </div>
            </div>

            <hr className="border-white/10 mb-4" />

            {selectedTicketGroups.length > 0 ? (
              <div className="space-y-3">
                {selectedTicketGroups.map((group) => (
                  <div key={group.tier.id} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                    <div className="flex justify-between text-sm text-white">
                      <span>
                        {group.seatCount}x {group.tier.section || group.tier.name}
                      </span>
                      <span>₱{formatDisplayAmount(group.subtotal)}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Seats: {group.seats.map((seat) => seat.label).join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-400">
                No ticket type selected.
              </div>
            )}

            <div className="flex justify-between font-bold text-lg mt-4">
              <span>TOTAL ({totalSeatCount} SEAT{totalSeatCount === 1 ? "" : "S"})</span>
              <span>₱{formatDisplayAmount(total)}</span>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="w-full border py-2 rounded"
              >
                Back
              </button>

              <button
                onClick={() => {
                  setShowModal(false);
                  setShowPaymentModal(true);
                }}
                className="w-full bg-red-600 py-2 rounded font-bold hover:bg-red-700"
              >
                Proceed to Payment
              </button>
            </div>

          </div>
        </div>
      )}

      {/* PAYMONGO CHECKOUT MODAL */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#2b2b2b] w-[90%] max-w-xl rounded-2xl p-6">
            <h2 className="text-center font-bold text-lg mb-6">
              SECURE PAYMENT
            </h2>

            <div className="bg-white/5 rounded-lg p-4 mb-4 text-sm">
              <p className="mb-1">
                <span className="text-gray-400">Event:</span> {event.title}
              </p>
              <p className="mb-1">
                <span className="text-gray-400">Tickets:</span>{" "}
                {totalSeatCount}
              </p>
              <p>
                <span className="text-gray-400">Amount:</span> ₱{formatDisplayAmount(total)}
              </p>
            </div>

            {selectedTicketGroups.length > 0 && (
              <div className="bg-white/5 rounded-lg p-3 mb-4 text-xs text-gray-300 space-y-1 max-h-28 overflow-y-auto">
                {selectedTicketGroups.map((group) => (
                  <p key={`pay-${group.tier.id}`}>
                    {group.tier.section || group.tier.name}: {group.seatCount} seat{group.seatCount === 1 ? "" : "s"} (₱{formatDisplayAmount(group.subtotal)})
                  </p>
                ))}
              </div>
            )}

            <p className="text-xs text-gray-400 mb-6">
              You will complete payment securely on PayMongo. Your ticket is issued only after payment is confirmed.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowModal(true);
                }}
                disabled={isPaying}
                className="w-full border py-2 rounded"
              >
                Back
              </button>

              <button
                onClick={handlePayMongoCheckout}
                disabled={isPaying}
                className="w-full bg-red-600 py-2 rounded font-bold hover:bg-red-700 disabled:opacity-60"
              >
                {isPaying ? "Processing..." : "Pay Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TICKET RECEIPT MODAL */}
      {showReceiptModal && activeTicket && (
        <div className="fixed inset-x-0 top-14 bottom-10 bg-black/45 flex items-center justify-center z-50 px-4">
          <div className="w-[390px] max-w-[92vw] max-h-[84vh] bg-black rounded-xl overflow-hidden shadow-2xl border border-red-600/60">
            <div className="bg-red-600 text-center py-2.5 font-bold text-base sm:text-lg relative uppercase tracking-wide">
              Ticket Details
              <button
                onClick={() => setShowReceiptModal(false)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/90 hover:text-white text-3xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-4 pb-4 pt-4 flex flex-col items-center overflow-y-auto no-scrollbar max-h-[calc(84vh-52px)]">
              <p className="text-[11px] text-white/70 mb-3 uppercase tracking-wide">
                Ticket {activeReceiptIndex + 1} of {createdTickets.length}
              </p>

              <div className="bg-[#efefef] text-black w-full rounded-2xl p-4 shadow-lg">
                <div className="flex justify-center mb-4">
                  <div className="bg-black text-white text-xs font-bold tracking-wide px-3 py-1">
                    <span className="text-red-600">ECHO</span>JHAM
                  </div>
                </div>

                <div className="text-base sm:text-xl font-bold uppercase text-center border-b border-black/15 pb-3 mb-4 leading-tight">
                  {event.title.toUpperCase()}
                </div>

                <div className="text-[11px] space-y-3 text-black/70 leading-snug">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide">Date</p>
                    <p className="text-black font-semibold">{event.date} | {event.time}</p>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wide">Venue</p>
                    <p className="text-black font-semibold uppercase">{event.venue}</p>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wide">Ticket Type</p>
                    <p className="text-black font-bold uppercase text-sm">{activeTicket.tierName}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-6 pt-1">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide">Section</p>
                      <p className="text-black font-semibold uppercase">{activeTicket.tierSection || activeTicket.tierName}</p>
                    </div>

                    <div>
                      <p className="text-[10px] uppercase tracking-wide">Row - Seat</p>
                      <p className="text-black font-semibold">{activeTicket.seatLabel || "-"}</p>
                    </div>
                  </div>

                  <div className="pt-1">
                    <p className="text-[10px] uppercase tracking-wide">Attendee</p>
                    <p className="text-black font-semibold">{activeTicket.attendeeName}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-[10px] uppercase tracking-wide">Order No.</p>
                      <p className="text-black font-semibold">{String(activeTicket.id)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wide">Price</p>
                      <p className="text-black font-semibold">₱{Number(activeTicket.tierPrice).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-dashed border-black/30 mt-6 pt-4">
                  <p className="text-center text-[10px] font-bold tracking-wider mb-3">TICKET ID</p>

                  <div className="flex justify-center">
                    <QRCode value={activeTicket.qr_code} style={{ width: 104, height: 104 }} />
                  </div>

                  <p className="text-center text-[10px] mt-2 font-semibold tracking-wide">
                    {activeTicket.manualTicketId}
                  </p>
                </div>

                <p className="text-center text-[9px] mt-2 text-black/60 break-all">
                  Backup QR: {activeTicket.qr_code}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 w-full mt-4">
                <button
                  onClick={handleDownloadSinglePdf}
                  disabled={isDownloadingPdf}
                  className="bg-red-600 py-2 rounded-md font-bold uppercase text-xs hover:bg-red-700 disabled:opacity-60"
                >
                  {isDownloadingPdf ? "Generating..." : "Download PDF"}
                </button>
                <button
                  onClick={handleDownloadAllPdf}
                  disabled={isDownloadingPdf}
                  className="bg-gray-600 py-2 rounded-md font-bold uppercase text-xs hover:bg-gray-500 disabled:opacity-60"
                >
                  {isDownloadingPdf ? "Preparing..." : "Download All"}
                </button>
              </div>

              <button
                onClick={() => setShowReceiptModal(false)}
                className="w-full mt-3 border border-white/30 text-white py-2 rounded-md text-sm font-semibold hover:bg-white/10"
              >
                Close
              </button>

              {hasMultipleReceipts && (
                <div className="grid grid-cols-3 items-center w-full mt-4 text-xs text-white/80">
                  <button
                    onClick={() => setActiveReceiptIndex((prev) => Math.max(prev - 1, 0))}
                    disabled={activeReceiptIndex === 0}
                    className="justify-self-start bg-slate-700/70 px-3 py-2 rounded disabled:opacity-40"
                  >
                    ← Previous
                  </button>

                  <p className="text-center text-white/60">Use arrows to navigate</p>

                  <button
                    onClick={() => setActiveReceiptIndex((prev) => Math.min(prev + 1, createdTickets.length - 1))}
                    disabled={activeReceiptIndex === createdTickets.length - 1}
                    className="justify-self-end bg-slate-700/70 px-3 py-2 rounded disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SeatPlanImageModal
        open={showSeatMapModal}
        onClose={() => setShowSeatMapModal(false)}
        title={event.title}
        imageUrl={event.seat_plan_image_url}
      />

    </div>
  );
};

export default EventDetails;